import {NewWasteFlavorFood} from "../../../rpc/webrpc-client.gen.ts";
import {createFileRoute} from "@tanstack/react-router";
import {BulkUploader} from "../../../upload/bulk-upload.tsx";
import {useTranslation} from "react-i18next";
import {JSX, useCallback} from "react";
import {WasteFlavorCapsule} from "../../../wasteFlavor/capsule.tsx";
import {UpsertWasteOrSurchargeFlavorResult, useUnitArmap, useUpsertWasteFlavorMutationOpts} from "../../../tq/tqx.tsx";
import {Sheet} from "@mui/joy";
import {z} from "zod/v4";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";
import {isTruthy} from "../../../tq/tqutil.tsx";

const wasteFlavorCsvHeaderTransformMap: Record<string, string> = {
    "新和品名CD": "code",
    "ソートNo": "sortName",
    "検索フラグドライバー": "showToDriver",
    "検索フラグ受付": "showToIntaker",
    "検索フラグ検収": "showToInspector",
    "検索フラグ特定業者": "",
    "品名一括登録フラグ": "includeInShortform",
    "請求品名": "displayName",
    "請求品名略称": "",
    "新和単位ＣＤ": "invoiceUnitCode",
    "単位": "invoiceUnit",
    "JWNET単位CD": "maniUnitCode",
    "JWNET単位": "manifestUnit",
    "マニ品名": "manifestName",
    "マニ略称": "",
    "分類コード": "",
    "分類名": "",
    "請求マニ換算用": "invoiceManiSlope",
    "請求マニ換算用b": "invoiceManiIntercept",
    "出力用ソートNo": "",
    "確認項目": "",
    "空白数": "",
    "チェック": "",
    "組成内訳フラグ": "birthable",
    "混合親フラグ": "divisible",
}

const CsvWasteFlavorFood = z.object({
    code: z.string(),
    displayName: z.string(),
    sortName: z.optional(z.string()),
    abbrev: z.optional(z.string()),
    invoiceUnit: z.optional(z.string()),
    invoiceUnitCode: z.optional(z.string()),
    manifestName: z.optional(z.string()),
    manifestAbbrev: z.optional(z.string()),
    manifestCode: z.optional(z.string()),
    manifestUnit: z.optional(z.string()),
    maniUnitCode: z.optional(z.string()),
    invoiceManiSlope: z.optional(z.string()),
    invoiceManiIntercept: z.optional(z.string()),
    showToDriver: z.optional(z.string()),
    showToIntaker: z.optional(z.string()),
    showToInspector: z.optional(z.string()),
    includeInShortform: z.optional(z.string()),
    birthable: z.optional(z.string()),
    divisible: z.optional(z.string()),
});
type CsvWasteFlavorFood = z.infer<typeof CsvWasteFlavorFood>;


function AdminWasteFlavor(): JSX.Element {
    const {t} = useTranslation();
    const upsertWasteFlavorTqmo = useUpsertWasteFlavorMutationOpts();
    const {data: units} = useUnitArmap();

    const csvWasteFlavorToNewWasteFlavorFood = useCallback((inCsv: CsvWasteFlavorFood) => {
            const csv = CsvWasteFlavorFood.parse(inCsv);
            const rv: NewWasteFlavorFood = {
                code: csv.code,
                displayName: csv.displayName,
            };
            if (csv.sortName) {
                rv.sortName = csv.sortName;
            }
            if (csv.abbrev) {
                rv.abbrev = csv.abbrev;
            }
            if (csv.invoiceUnitCode) {
                const unit = units?.byCode.get(csv.invoiceUnitCode);
                if (!unit) {
                    throw new Error(`no such unit code ${csv.invoiceUnitCode}`);
                }
                rv.invoiceUnitId = unit.id;
            }
            if (csv.manifestName) {
                rv.manifestName = csv.manifestName;
            }
            rv.manifestAbbrev = csv.manifestAbbrev ?? csv.abbrev;
            if (csv.manifestCode) {
                rv.manifestCode = csv.manifestCode;
            }
            if (csv.maniUnitCode) {
                const unit = units?.byCode.get(csv.maniUnitCode);
                if (!unit) {
                    throw new Error(`no such unit code ${csv.maniUnitCode}`);
                }
                rv.manifestUnitId = unit.id;
            }
            if (csv.invoiceManiSlope) {
                rv.invoiceManiSlope = parseFloat(csv.invoiceManiSlope);
            }
            if (csv.invoiceManiIntercept) {
                rv.invoiceManiIntercept = parseFloat(csv.invoiceManiIntercept);
            }
            if (isTruthy(csv.showToDriver)) {
                rv.showToDriver = true;
            }
            if (isTruthy(csv.showToIntaker)) {
                rv.showToIntaker = true;
            }
            if (isTruthy(csv.showToInspector)) {
                rv.showToInspector = true;
            }
            if (isTruthy(csv.includeInShortform)) {
                rv.includeInShortform = true;
            }
            if (isTruthy(csv.birthable)) {
                rv.birthable = true;
            }
            if (isTruthy(csv.divisible)) {
                rv.divisible = true;
            }
            return rv;
        }, [units?.byCode]);

    return <PageShell>
        <PageHeader><TopBar/></PageHeader>
        <PageBody>
            <BulkUploader<CsvWasteFlavorFood, NewWasteFlavorFood, UpsertWasteOrSurchargeFlavorResult>
                entityTypeLabel={t("entity.wasteFlavor")}
                headerTransformMap={wasteFlavorCsvHeaderTransformMap}
                csvToUpsertFood={csvWasteFlavorToNewWasteFlavorFood}
                upsertTqo={upsertWasteFlavorTqmo}
                upsertFoodCapsule={(food) =>
                    <Sheet color="primary" variant="soft">
                        <WasteFlavorCapsule entity={{displayName: food.displayName, code: food.code}}/>
                    </Sheet>}
                existingId={({result}) => result.existingId ?? null}
                newId={({result}) => result.newId ?? null}
                completedCapsule={({food, color, variant}) =>
                    <Sheet color={color} variant={variant}>
                        <WasteFlavorCapsule
                            entity={{displayName: food.displayName, code: food.code}}/>
                    </Sheet>}
                conflictCapsules={({result: tqr}) => {
                    if (tqr.conflict) {
                        return [
                            <WasteFlavorCapsule
                                key="waste-flavor"
                                entity={{
                                    displayName: tqr.conflict.displayName,
                                    code: tqr.conflict.code
                                }}/>
                        ]
                    } else {
                        return null;
                    }
                }
                }
            />
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/admin/waste-flavor"
)({
    component: AdminWasteFlavor,
});
